import React from 'react';
import { Icons } from '@/src/modules/components/Icons';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { InteractiveImageAccordion } from '@/src/modules/components/ui/interactive-image-accordion';
import { EndlessWaysSection } from '@/src/modules/components/EndlessWays/EndlessWaysSection';
import { HeroCTAButton } from '@/src/modules/components/ui/hero-cta-button';
import { publicAssetUrl } from '@/src/infrastructure/media';
import { useAuth } from '@/src/modules/auth/AuthProvider';

const occasions = [
  { name: 'Weddings', slug: 'wedding', image: publicAssetUrl('public/assets/occasions/weddings.png') },
  { name: 'Birthdays', slug: 'birthday', image: publicAssetUrl('public/assets/occasions/birthdays.png') },
  { name: 'Corporate', slug: 'custom', image: publicAssetUrl('public/assets/occasions/corporate.png') },
  { name: 'Dinner Parties', slug: 'custom', image: publicAssetUrl('public/assets/occasions/dinner_parties.png') },
  { name: 'Baby Showers', slug: 'baby_shower', image: publicAssetUrl('public/assets/occasions/baby_showers.png') },
  { name: 'Graduations', slug: 'send_off', image: publicAssetUrl('public/assets/occasions/graduations.png') },
  { name: 'Holidays', slug: 'custom', image: publicAssetUrl('public/assets/occasions/holidays.png') },
];

export function Home() {
  const { user } = useAuth();
  const heroCtaHref = user ? '/wizard' : '/login?returnTo=%2Fwizard';

  return (
    <div className="flex flex-col w-full overflow-hidden">
      {/* Hero Section with Interactive Image Accordion */}
      <section data-section="hero" className="relative pt-20 pb-16 lg:pt-28 lg:pb-24 overflow-hidden">
        <div className="absolute top-0 right-0 -mr-[20%] -mt-[10%] w-[60%] h-[80%] rounded-full bg-primary/20 blur-[100px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 -ml-[10%] -mb-[10%] w-[40%] h-[60%] rounded-full bg-status-processing/10 blur-[80px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <motion.div
              data-testid="hero-copy-motion"
              initial={{ opacity: 0.94 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.35 }}
              className="w-full lg:w-2/5 flex flex-col items-start text-left"
            >
              <span className="inline-block py-1 px-3 rounded-full bg-primary/10 text-primary font-bold text-xs mb-6">
                ✨ New Templates Available
              </span>
              <h1 className="text-4xl lg:text-5xl xl:text-6xl leading-tight font-bold text-on-surface mb-6 tracking-tight">
                Create beautiful <span className="text-primary text-glow">invitations</span> in minutes.
              </h1>
              <p className="text-lg text-on-surface-variant mb-8 max-w-md">
                Design, send, and track RSVPs for your next event with our modern, easy-to-use platform. No design skills required.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <Link
                  to={heroCtaHref}
                  state={user ? undefined : { from: '/wizard' }}
                  data-track="hero-start-cta"
                >
                  <HeroCTAButton />
                </Link>
              </div>
            </motion.div>

            <motion.div
              data-testid="hero-visual-motion"
              initial={{ opacity: 0.94 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.35, delay: 0.1 }}
              className="w-full lg:w-3/5"
            >
              <InteractiveImageAccordion autoPlayInterval={4000} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Endless Ways to Spread the Joy Section */}
      <EndlessWaysSection />

      {/* Occasions Section */}
      <section data-section="occasions" className="py-24 bg-surface-lowest">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-on-surface mb-4">Every occasion covered</h2>
            <p className="text-xl text-on-surface-variant max-w-2xl mx-auto">Start with a stunning template designed specifically for your event type.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {occasions.map((occasion) => (
              <Link
                key={occasion.name}
                to={`/catalog?occasion=${occasion.slug}`}
                className="block"
              >
                <motion.div
                  whileHover={{ y: -8 }}
                  className="group relative overflow-hidden rounded-2xl aspect-[4/3] cursor-pointer"
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent z-10 transition-opacity duration-300 group-hover:opacity-60" />
                  <img 
                    src={occasion.image} 
                    alt={occasion.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute bottom-0 left-0 p-6 z-20 w-full">
                    <h3 className="text-xl text-white font-bold">{occasion.name}</h3>
                  </div>
                </motion.div>
              </Link>
            ))}
            
            <Link to="/catalog" className="block">
              <motion.div
                whileHover={{ y: -8 }}
                className="group flex flex-col items-center justify-center p-6 rounded-2xl bg-surface-low border border-outline-variant/30 aspect-[4/3] cursor-pointer hover:bg-primary/5 transition-colors h-full"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4 group-hover:scale-110 transition-transform shadow-sm">
                  <Icons.Dashboard size={24} />
                </div>
                <h3 className="text-lg font-bold text-on-surface text-center">View All Categories</h3>
                <p className="text-xs text-on-surface-variant mt-2 font-medium">Explore all events</p>
              </motion.div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
